/**
 * update 命名空间字典（i18n 分区）
 *
 * 版本与更新：管理面板「通用」的版本卡片 + 桌面端顶部提示条共用。
 * 这里只放展示文案；版本号本身与「有新版本」的判定由后端给
 * （/api/system/update-status，见 backend/app/services/infrastructure/update_check.py）。
 */

import type { TranslationDict } from '../types'

export const updateZh: TranslationDict = {
  // ======================== 版本与更新 / Updates ========================
  'title': '版本与更新',
  'hint': '显示当前版本与官方最新版本。检查在后台完成，每小时最多访问一次上游，不影响使用。',
  'current': '当前版本',
  'latestLabel': '最新版本',
  'upToDate': '已是最新',
  'updateAvailable': '有新版本',
  'unknownVersion': '未知',
  'cannotCheck': '无法检查更新',
  'disabled': '已关闭更新检查',
  'disabledHint': '部署方关闭了出站检查（UPDATE_CHECK_ENABLED=false）。可手动打开发布页确认最新版本。',
  'checking': '检查中…',
  'checkNow': '立即检查',
  'justChecked': '刚检查过，稍后再试',
  'releaseNotes': '更新说明',
  'viewRelease': '打开发布页',
  'download': '下载安装包',
  'publishedAt': '发布于 {date}',
  'checkedAt': '检查于 {time}',
  'channelBeta': '渠道：测试版（预发布也算）',
  'staleHint': '上游暂时不可达，以下是最新一次成功检查的结果',
  'upgradeTitle': '怎么升到这个版本',
  'upgradeDocker': '在部署目录执行（会拉取新镜像并重建容器，数据在数据卷里不受影响）：',
  'upgradeDesktop': '下载安装包后直接运行，选「覆盖安装」即可。聊天记录与配置在数据目录，不会丢。',
  'upgradeSource': '在代码目录执行下列命令，然后重启后端：',
  'copy': '复制',
  'copied': '已复制',
  'dismiss': '关闭',
  'bannerText': 'Copree 有新版本 {version}',
  'bannerAction': '查看',
  'errorTimeout': '连接上游超时',
  'errorUnreachable': '网络不通（离线或被墙）',
  'errorRateLimited': '请求过于频繁，稍后再试',
  'errorNotFound': '找不到发布信息',
  'errorBadResponse': '响应格式无法识别',
  'errorDisabled': '已关闭更新检查',
  'errorGeneric': '检查失败',
}

export const updateEn: TranslationDict = {
  'title': 'Version & Updates',
  'hint': 'Shows the version you run and the official latest. Checks run in the background, at most once an hour upstream, and never block the UI.',
  'current': 'Current',
  'latestLabel': 'Latest',
  'upToDate': 'Up to date',
  'updateAvailable': 'Update available',
  'unknownVersion': 'Unknown',
  'cannotCheck': 'Cannot check for updates',
  'disabled': 'Update check disabled',
  'disabledHint': 'The operator disabled outbound checks (UPDATE_CHECK_ENABLED=false). Open the release page to confirm the latest version.',
  'checking': 'Checking…',
  'checkNow': 'Check now',
  'justChecked': 'Checked recently — try again shortly',
  'releaseNotes': 'Release notes',
  'viewRelease': 'Open release page',
  'download': 'Download installer',
  'publishedAt': 'Published {date}',
  'checkedAt': 'Checked {time}',
  'channelBeta': 'Channel: beta (pre-releases included)',
  'staleHint': 'Upstream is unreachable right now — showing the last successful check',
  'upgradeTitle': 'How to upgrade',
  'upgradeDocker': 'Run this in the deploy directory (pulls the new image and recreates the container; data lives in volumes and is untouched):',
  'upgradeDesktop': 'Run the downloaded installer and choose overwrite. Chats and settings live in the data directory and are kept.',
  'upgradeSource': 'Run the following in the code directory, then restart the backend:',
  'copy': 'Copy',
  'copied': 'Copied',
  'dismiss': 'Dismiss',
  'bannerText': 'Copree {version} is available',
  'bannerAction': 'View',
  'errorTimeout': 'Timed out contacting upstream',
  'errorUnreachable': 'Network unreachable (offline or blocked)',
  'errorRateLimited': 'Rate limited — try again later',
  'errorNotFound': 'No release information found',
  'errorBadResponse': 'Unrecognised response format',
  'errorDisabled': 'Update check disabled',
  'errorGeneric': 'Check failed',
}

export const updateJa: TranslationDict = {
  'title': 'バージョンと更新',
  'hint': '現在のバージョンと公式の最新版を表示します。確認はバックグラウンドで行い、上流へのアクセスは 1 時間に最大 1 回です。',
  'current': '現在',
  'latestLabel': '最新',
  'upToDate': '最新です',
  'updateAvailable': '更新があります',
  'unknownVersion': '不明',
  'cannotCheck': '更新を確認できません',
  'disabled': '更新確認は無効です',
  'disabledHint': '運用者が外部チェックを無効にしています（UPDATE_CHECK_ENABLED=false）。リリースページで最新版を確認してください。',
  'checking': '確認中…',
  'checkNow': '今すぐ確認',
  'justChecked': '確認したばかりです。しばらくしてからお試しください',
  'releaseNotes': 'リリースノート',
  'viewRelease': 'リリースページを開く',
  'download': 'インストーラーをダウンロード',
  'publishedAt': '公開 {date}',
  'checkedAt': '確認 {time}',
  'channelBeta': 'チャンネル：ベータ（プレリリースを含む）',
  'staleHint': '上流に接続できないため、最後に成功した確認結果を表示しています',
  'upgradeTitle': '更新方法',
  'upgradeDocker': 'デプロイディレクトリで実行します（新しいイメージを取得してコンテナを再作成します。データはボリューム内で保持されます）：',
  'upgradeDesktop': 'ダウンロードしたインストーラーを実行し、上書きインストールを選びます。会話と設定はデータディレクトリにあり保持されます。',
  'upgradeSource': 'コードディレクトリで次を実行し、バックエンドを再起動します：',
  'copy': 'コピー',
  'copied': 'コピーしました',
  'dismiss': '閉じる',
  'bannerText': 'Copree {version} が公開されました',
  'bannerAction': '見る',
  'errorTimeout': '上流への接続がタイムアウトしました',
  'errorUnreachable': 'ネットワークに到達できません（オフラインまたは遮断）',
  'errorRateLimited': 'リクエストが多すぎます。後でお試しください',
  'errorNotFound': 'リリース情報が見つかりません',
  'errorBadResponse': '応答形式を解釈できません',
  'errorDisabled': '更新確認は無効です',
  'errorGeneric': '確認に失敗しました',
}
